import {
  startTransition,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { sanitizeDeepStringValues } from "../utils/textSecurity";
import { mark, measure } from "../utils/perfMarks";
import {
  loadEventsRuntimeData,
  loadMyEventsRuntimeData,
  loadMoreMyEventsRuntimeData,
  loadSecretEventsRuntimeData,
} from "../data/runtimeDataSource";
import { useAuth } from "../core/auth/AuthContext";
import {
  createRemoteEvent,
  clearAllStoredEventAccessCodes,
  clearStoredEventAccessCode,
  getMyRemoteEventById,
  getRemoteEvent,
  storeEventAccessCode,
  updateRemoteEvent,
} from "../core/api/karangoApi";
import { mergeUniqueEventsById } from "../features/events/mergeEventLists";
import { isSecretEvent, isUpcomingEvent } from "../features/events/eventVisibility";
import { DEEP_LINK_TYPES, parseDeepLinkFromLocation } from "../navigation/deepLinks";
import { isPointInsideRadiusKm } from "../utils/geoDistance";
import { readShareAttribution, trackConversionEvent } from "../core/analytics/conversionAnalytics";
import { DEFAULT_SEARCH_RADIUS_KM } from "../features/location/memberZone.constants";

// Delai avant de rafraichir la sortie qu'on vient d'ouvrir : laisse finir
// l'animation d'ouverture de la fiche (≈ 300 ms) sans re-rendu concurrent.
const SELECTED_EVENT_REFRESH_DELAY_MS = 360;

export function buildInitialDiscoveryArea() {
  // "Toute la France" est le mode par defaut assume, pas un etat transitoire
  // en attente de resolution : l'utilisateur choisit explicitement "Autour
  // de moi" / "Choisir une zone sur la carte" / "Populaires" s'il veut autre
  // chose (cf. menu de zone, MobileLayout.js). Avant ce changement, l'etat
  // initial etait `null` le temps qu'un effet de MapView applique
  // automatiquement un cercle autour de la position profil/cache des qu'elle
  // etait disponible — ce qui produisait un affichage "Choisis une zone"
  // frequent et une zone changee silencieusement sans action de
  // l'utilisateur. Partir directement de "national" supprime les deux :
  // l'effet de MapView (cf. hasAutoAppliedRef) se voit immediatement un
  // eventDiscoveryArea deja resolu et ne fait plus rien.
  return { type: "national", scope: "france", appliedAt: Date.now() };
}

// Arrivee par le lien partage /listevents : la premiere requete charge
// directement les sorties populaires (pas de chargement « Toute la France »
// puis bascule). Sinon, vue par defaut.
export function resolveInitialDiscoveryArea(initialDeepLink) {
  return initialDeepLink?.type === DEEP_LINK_TYPES.EVENTS_LIST
    ? { type: "popular", scope: "france", appliedAt: Date.now() }
    : buildInitialDiscoveryArea();
}

export function buildDiscoveryQuery(area) {
  if (area?.type === "map_circle") {
    return {
      lat: area.center.lat,
      lng: area.center.lng,
      radiusKm: area.radiusKm,
    };
  }

  if (area?.type === "nearest") {
    return {
      sort: "nearest",
      lat: area.center.lat,
      lng: area.center.lng,
    };
  }

  if (area?.type === "popular") {
    return { sort: "popular" };
  }

  // "national" (par defaut, y compris area === null tant que la position
  // n'est pas resolue) : aucun filtre geo ni de popularite, tri chronologique
  // national — distinct de "popular" qui exige desormais au moins un
  // participant en plus de l'organisateur (cf. buildPopularEventsWhere).
  return {};
}

function isAbortError(error) {
  return error?.name === "AbortError";
}

function isEventAccessLocked(event = {}) {
  return Boolean(event?.access?.locked);
}

function normalizeRole(value) {
  return String(value || "").trim().toUpperCase();
}

export function upsertEventInList(events = [], event, { preservePosition = false } = {}) {
  if (!event?.id) {
    return Array.isArray(events) ? events : [];
  }

  const safeEvents = Array.isArray(events) ? events : [];
  const existingIndex = safeEvents.findIndex((eventItem) => eventItem.id === event.id);

  if (preservePosition) {
    if (existingIndex < 0) {
      return [...safeEvents, event];
    }

    const nextEvents = safeEvents.slice();
    nextEvents[existingIndex] = event;
    return nextEvents;
  }

  const eventsWithoutDuplicate = safeEvents.filter(
    (eventItem) => eventItem.id !== event.id
  );

  return [event, ...eventsWithoutDuplicate];
}

// ADR-005 §9 : une fiche verrouillee remplace entierement la fiche ouverte —
// jamais de fusion avec d'anciennes donnees completes (adresse, participants...).
export function applyRefreshedEventToSelection(previousSelectedEvent, event) {
  if (!previousSelectedEvent || !event?.id || previousSelectedEvent.id !== event.id) {
    return previousSelectedEvent;
  }

  return isEventAccessLocked(event) ? event : mergeEventPatch(previousSelectedEvent, event);
}

// ADR-005 §9 : chaque changement de compte (connexion, deconnexion, A -> B,
// y compris A -> B -> A) ouvre une nouvelle session. Une requete demarree dans
// une session precedente ne modifie plus rien : ni donnees, ni statut de
// chargement, ni indicateur d'erreur.
export function isStaleSessionResponse(requestGeneration, currentGeneration) {
  return requestGeneration !== currentGeneration;
}

// ADR-005 §9 : 403/404 signifient que le lecteur n'a pas (ou plus) acces a la
// sortie ; les autres erreurs (reseau, 5xx) ne doivent rien purger.
export function isAccessLossError(error) {
  return error?.status === 403 || error?.status === 404;
}

// ADR-005 §9 : purge des donnees privees au changement de compte et a la
// deconnexion. La connexion d'un visiteur anonyme (null -> uid) ne purge pas :
// c'est la meme personne, qui a pu saisir un code juste avant.
export function shouldPurgeOnAccountTransition(previousUid, nextUid) {
  return Boolean(previousUid) && previousUid !== (nextUid || null);
}

function removeEventFromList(events = [], eventId) {
  if (!eventId) {
    return Array.isArray(events) ? events : [];
  }

  return (Array.isArray(events) ? events : []).filter(
    (eventItem) => eventItem.id !== eventId
  );
}

function sameEventIds(a, b) {
  if (a === b) return true;
  if (!a || !b || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i]?.id !== b[i]?.id) return false;
  }
  return true;
}

export function filterVisibleUpcomingEvents(events = [], mapBounds, { secret = false, eventDiscoveryArea = null } = {}) {
  const sourceEvents = Array.isArray(events) ? events : [];
  if (!sourceEvents.length) return [];

  const nowDate = new Date();
  const circleActive = eventDiscoveryArea?.type === "map_circle";
  const rankedDiscoveryActive =
    eventDiscoveryArea?.type === "popular" ||
    eventDiscoveryArea?.type === "nearest" ||
    eventDiscoveryArea?.type === "national";

  return sourceEvents.filter((event) => {
    if (isSecretEvent(event) !== secret) {
      return false;
    }

    if (!isUpcomingEvent(event, nowDate)) {
      return false;
    }

    // Les flux classés sont nationaux et déjà ordonnés par le serveur. Les
    // limites du viewport ne doivent pas en masquer une partie.
    if (rankedDiscoveryActive) {
      return true;
    }

    // Quand une zone cercle est appliquee, on ignore le rectangle de viewport
    // et on applique un garde-fou Haversine cote client.
    if (circleActive) {
      const lat = event?.location?.lat;
      const lng = event?.location?.lng;
      if (typeof lat !== "number" || typeof lng !== "number") return false;
      return isPointInsideRadiusKm(
        { lat, lng },
        eventDiscoveryArea.center,
        eventDiscoveryArea.radiusKm
      );
    }

    if (!mapBounds) {
      return true;
    }

    const { north, south, east, west } = mapBounds;
    const lat = event?.location?.lat;
    const lng = event?.location?.lng;

    if (typeof lat !== "number" || typeof lng !== "number") return false;

    return (
      lat >= south &&
      lat <= north &&
      lng >= west &&
      lng <= east
    );
  });
}

/**
 * ======================================================
 * APP CONTEXT — ÉTAT GLOBAL MÉTIER (ULTRA ULTIME)
 * ======================================================
 *
 * 🎯 RÔLE
 * ------------------------------------------------------
 * - Source de vérité métier de l’application
 * - Centralise :
 *   • données
 *   • sélections
 *   • intentions (actions)
 *
 * ❌ NE FAIT PAS
 * ------------------------------------------------------
 * - logique UI
 * - logique animation
 * - logique layout
 * - details reseau bas niveau
 *
 * 🧠 PHILOSOPHIE
 * ------------------------------------------------------
 * - état SIMPLE
 * - intentions explicites
 * - aucune donnée redondante
 */

const AppContext = createContext(null);

/* ======================================================
   OUTILS INTERNES
====================================================== */

function mergeEventPatch(currentEvent, patch = {}) {
  return {
    ...currentEvent,
    ...patch,
    location: patch.location
      ? {
          ...(currentEvent.location || {}),
          ...patch.location,
        }
      : currentEvent.location,
    meetingPoint: patch.meetingPoint
      ? {
          ...(currentEvent.meetingPoint || {}),
          ...patch.meetingPoint,
        }
      : currentEvent.meetingPoint,
  };
}

/* ======================================================
   PROVIDER
====================================================== */

export function AppProvider({ children }) {
  const { user: authUser, loading: authLoading } = useAuth();
  const initialDeepLinkRef = useRef(
    typeof window !== "undefined" ? parseDeepLinkFromLocation(window.location) : null
  );
  // Priorité aux custom claims JWT (source de vérité sécurisée).
  // Fallback sur profile.role pendant le premier bootstrap (claims pas encore chargés).
  const resolvedRole = normalizeRole(authUser?.claims?.role ?? authUser?.role);
  const canViewSecretEvents = resolvedRole === "ADMIN";

  const _userCoords = authUser?.location?.coordinates;
  const userGeoLng = Array.isArray(_userCoords) ? Number(_userCoords[0]) : null;
  const userGeoLat = Array.isArray(_userCoords) ? Number(_userCoords[1]) : null;
  const userMaxDist = Number(authUser?.maxDistance) || DEFAULT_SEARCH_RADIUS_KM;

  /* ====================================================
     DONNÉES MÉTIER
  ==================================================== */

  /** Liste complète des événements */
  const [events, setEvents] = useState([]);
  const [isEventsLoading, setIsEventsLoading] = useState(true);
  const eventsRequestGenerationRef = useRef(0);
  /** Historique personnel (Agenda) : sorties organisees/rejointes, toutes
   * dates confondues — distinct de `events` (decouverte active uniquement
   * depuis le filtre temporel SQL, cf. C21 de l'audit). Charge a la demande
   * (premiere ouverture de l'Agenda), pas au login, pour ne pas concurrencer
   * le chargement initial de la carte (cf. section 21/22 du contre-audit). */
  const [historyEvents, setHistoryEvents] = useState([]);
  const [historyEventsStatus, setHistoryEventsStatus] = useState("idle"); // idle | loading | ready | error
  const [historyEventsNextCursor, setHistoryEventsNextCursor] = useState(null);
  const [isHistoryEventsLoadingMore, setIsHistoryEventsLoadingMore] = useState(false);
  const isHistoryEventsLoading = historyEventsStatus === "loading";
  const hasLoadedHistoryEvents = historyEventsStatus === "ready";
  // Lu par `ensureHistoryEventsLoaded` pour rester STABLE entre deux rendus :
  // si ce callback changeait d'identite a chaque transition idle->loading,
  // les effets qui l'ont en dependance (cf. MobileLayout/DesktopLayout,
  // ouverture de l'Agenda) se re-declenchaient et leur cleanup abortait la
  // requete en cours avant qu'elle ne se termine, bloquant le statut sur
  // "loading" pour toujours (skeleton infini — bug constate en prod).
  const historyEventsStatusRef = useRef(historyEventsStatus);
  useEffect(() => {
    historyEventsStatusRef.current = historyEventsStatus;
  }, [historyEventsStatus]);
  const [secretEvents, setSecretEvents] = useState([]);
  const [isSecretEventsLoading, setIsSecretEventsLoading] = useState(false);
  const [hasLoadedSecretEvents, setHasLoadedSecretEvents] = useState(false);

  /* ====================================================
     SÉLECTIONS GLOBALES
  ==================================================== */

  /** Event sélectionné (source de vérité UI) */
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventDetailRequests, setEventDetailRequests] = useState({});
  const eventDetailRequestSequenceRef = useRef(0);
  const sharedLoadedSelectionRef = useRef(null);
  // Compte courant, lu par les requetes asynchrones pour ignorer une reponse
  // demarree avec un autre compte (ADR-005 §9).
  const authUidRef = useRef(authUser?.uid || null);
  // Numero de session, incremente a chaque changement de compte (y compris
  // A -> B -> A) : une requete d'une session precedente n'applique plus rien.
  const sessionGenerationRef = useRef(0);
  const pendingSharedEventIdRef = useRef(
    initialDeepLinkRef.current?.type === "event"
      ? initialDeepLinkRef.current.eventId
      : null
  );
  const sharedLandingTrackedRef = useRef(false);
  const [pendingDeepLink, setPendingDeepLink] = useState(() => {
    const initialDeepLink = initialDeepLinkRef.current;
    return initialDeepLink?.type &&
      // /listevents agit seulement sur la vue initiale (ci-dessous) : rien a
      // traiter ensuite par les layouts.
      initialDeepLink.type !== DEEP_LINK_TYPES.EVENTS_LIST &&
      (initialDeepLink.type !== "event" ||
        initialDeepLink.subPath ||
        initialDeepLink.notificationId)
      ? initialDeepLink
      : null;
  });
  const consumePendingDeepLink = useCallback(() => {
    setPendingDeepLink(null);
  }, []);

  /* ====================================================
     ÉTAT CARTE (TECHNIQUE)
  ==================================================== */

  /** Bounds visibles de la carte */
  const [mapBounds, setMapBounds] = useState(null);

  /** Zoom courant de la carte (mis à jour uniquement sur moveend) */
  const [mapZoom, setMapZoom] = useState(null);

  /** Mode de découverte : populaire par défaut, plus proche après consentement
   *  GPS, ou cercle lorsque la personne recherche explicitement sur la carte. */
  const [eventDiscoveryArea, setEventDiscoveryArea] = useState(() =>
    resolveInitialDiscoveryArea(initialDeepLinkRef.current)
  );
  const eventDiscoveryAreaRef = useRef(eventDiscoveryArea);
  useEffect(() => {
    eventDiscoveryAreaRef.current = eventDiscoveryArea;
  }, [eventDiscoveryArea]);

  const refreshEvents = useCallback(
    async ({ signal, silent = false, geoOverride = null } = {}) => {
      const requestGeneration = ++eventsRequestGenerationRef.current;
      const requestSession = sessionGenerationRef.current;
      const isLatestRequest = () =>
        requestGeneration === eventsRequestGenerationRef.current &&
        requestSession === sessionGenerationRef.current;

      if (!silent) {
        setIsEventsLoading(true);
      }

      // Affiche la 1ere page recue sans attendre les suivantes (jusqu'a 4
      // pages sequentielles sinon) — cf. docs/map-performance-mobile-scroll-audit.md, Phase 3.
      // Uniquement pour un chargement non silencieux : un refresh silencieux
      // (changement de zone geo en arriere-plan) ne doit jamais remplacer
      // temporairement la liste complete deja affichee par les 50 premiers
      // events de la nouvelle reponse (regression corrigee, cf. C23).
      let hasShownFirstPage = false;
      let accumulatedEventsSnapshot = [];

      try {
        // Un override est utilisé par les actions atomiques ci-dessous ; sinon
        // le mode courant détermine intégralement la requête.
        const area = eventDiscoveryAreaRef.current;
        const query = geoOverride ?? buildDiscoveryQuery(area);
        const payload = await loadEventsRuntimeData({
          signal,
          ...query,
          // DTO leger pour le menu Evenements (carte/liste/carrousel) — cf.
          // docs/event-menu-apple-like-performance-execution-spec.md, phase 2.
          // Seul ce flux (la decouverte principale) migre ; l'historique
          // (/v1/events/mine), le scope secret et les ecrans admin
          // (AdminModal/AdminSimulatorTab, timeScope=all) continuent
          // d'appeler le DTO complet, inchange.
          view: "discovery",
          onPage: silent
            ? undefined
            : ({ events: accumulatedEvents }) => {
                if (signal?.aborted || !isLatestRequest()) return;
                accumulatedEventsSnapshot = accumulatedEvents;
                if (!hasShownFirstPage) {
                  mark("events_first_page_received");
                  measure(
                    "events_request_to_first_page",
                    "events_request_start",
                    "events_first_page_received"
                  );
                }
                hasShownFirstPage = true;
                startTransition(() => {
                  setEvents(accumulatedEvents);
                  setIsEventsLoading(false);
                });
              },
        });

        if (signal?.aborted) {
          return { status: "aborted" };
        }

        if (!isLatestRequest()) {
          return { status: "stale" };
        }

        startTransition(() => {
          setEvents(payload.events || []);
          setIsEventsLoading(false);
        });

        return { status: "success", events: payload.events || [] };
      } catch (error) {
        if (isAbortError(error) || signal?.aborted) {
          return { status: "aborted" };
        }

        if (!isLatestRequest()) {
          return { status: "stale" };
        }

        console.error("Erreur chargement events:", error);
        if (hasShownFirstPage) {
          setIsEventsLoading(false);
          return {
            status: "success",
            events: accumulatedEventsSnapshot,
            partial: true,
          };
        }

        if (!silent) {
          // Une page suivante en echec ne doit pas effacer une 1ere page deja affichee.
          setEvents([]);
        }
        setIsEventsLoading(false);

        return { status: "error", error };
      }
    },
    []
  );

  const applyMapCircleSearchArea = useCallback(
    async ({ center, radiusKm, signal } = {}) => {
      if (
        !Number.isFinite(center?.lat) ||
        !Number.isFinite(center?.lng) ||
        !Number.isFinite(radiusKm) ||
        radiusKm <= 0
      ) {
        return { status: "error", error: new Error("Zone invalide") };
      }
      const area = {
        type: "map_circle",
        center: { lat: center.lat, lng: center.lng },
        radiusKm,
        appliedAt: Date.now(),
      };
      setEventDiscoveryArea(area);
      eventDiscoveryAreaRef.current = area;
      return refreshEvents({ signal, silent: false, geoOverride: { lat: center.lat, lng: center.lng, radiusKm } });
    },
    [refreshEvents]
  );

  const applyNearestEventsDiscovery = useCallback(
    async ({ center, locationSource, signal } = {}) => {
      if (!Number.isFinite(center?.lat) || !Number.isFinite(center?.lng)) {
        return { status: "error", error: new Error("Position invalide"), events: [] };
      }

      const area = {
        type: "nearest",
        center: { lat: center.lat, lng: center.lng },
        locationSource: locationSource === "profile" ? "profile" : locationSource === "gps" ? "gps" : null,
        appliedAt: Date.now(),
      };
      setEventDiscoveryArea(area);
      eventDiscoveryAreaRef.current = area;
      return refreshEvents({
        signal,
        silent: false,
        geoOverride: { sort: "nearest", lat: center.lat, lng: center.lng },
      });
    },
    [refreshEvents]
  );

  // Toute la France, sans filtre de popularite : c'est le mode par defaut
  // (cf. buildInitialDiscoveryArea) et l'option explicite "Toute la France"
  // du menu de zone.
  const applyNationalEventsDiscovery = useCallback(
    async ({ signal } = {}) => {
      const area = { type: "national", scope: "france", appliedAt: Date.now() };
      setEventDiscoveryArea(area);
      eventDiscoveryAreaRef.current = area;
      return refreshEvents({
        signal,
        silent: false,
        geoOverride: {},
      });
    },
    [refreshEvents]
  );

  // Toute la France, restreint aux sorties ayant au moins un participant en
  // plus de l'organisateur (cf. buildPopularEventsWhere cote backend).
  const applyPopularEventsDiscovery = useCallback(
    async ({ signal } = {}) => {
      const area = { type: "popular", scope: "france", appliedAt: Date.now() };
      setEventDiscoveryArea(area);
      eventDiscoveryAreaRef.current = area;
      return refreshEvents({
        signal,
        silent: false,
        geoOverride: { sort: "popular" },
      });
    },
    [refreshEvents]
  );

  const clearMapCircleSearchArea = useCallback(
    async ({ signal } = {}) => {
      return applyNationalEventsDiscovery({ signal });
    },
    [applyNationalEventsDiscovery]
  );

  // Historique personnel (Agenda) : sorties organisees/rejointes, toutes
  // dates confondues — appelle GET /v1/events/mine (authentifie), distinct
  // du flux de decouverte actif `events` (cf. C21 de l'audit perf/scroll).
  // Charge la PREMIERE page uniquement ; `loadMoreHistoryEvents` gere la suite.
  const loadHistoryEventsFirstPage = useCallback(
    async ({ signal } = {}) => {
      if (!authUser?.uid) {
        startTransition(() => {
          setHistoryEvents([]);
          setHistoryEventsNextCursor(null);
          setHistoryEventsStatus("idle");
        });
        return { status: "skipped" };
      }

      setHistoryEventsStatus("loading");
      // ADR-005 §9 : une requete d'une session precedente ne touche plus ni
      // l'historique (sorties privees comprises) ni son statut, en succes
      // comme en erreur ; l'effet de changement de compte a remis l'etat a zero.
      const requestGeneration = sessionGenerationRef.current;
      const isStale = () => isStaleSessionResponse(requestGeneration, sessionGenerationRef.current);

      try {
        const page = await loadMyEventsRuntimeData({ signal });

        if (isStale()) {
          return { status: "aborted" };
        }

        if (signal?.aborted) {
          // Remet "idle" (et non "loading") pour qu'un appel ulterieur
          // puisse reessayer — sinon le statut reste bloque sur "loading"
          // indefiniment (skeleton infini) si cette requete est abandonnee
          // avant d'avoir pu aboutir.
          setHistoryEventsStatus("idle");
          return { status: "aborted" };
        }

        startTransition(() => {
          setHistoryEvents(page.events || []);
          setHistoryEventsNextCursor(page.nextCursor || null);
          setHistoryEventsStatus("ready");
        });

        return { status: "success" };
      } catch (error) {
        if (isStale()) {
          return { status: "aborted" };
        }

        if (isAbortError(error) || signal?.aborted) {
          setHistoryEventsStatus("idle");
          return { status: "aborted" };
        }

        console.error("Erreur chargement historique personnel:", error);
        setHistoryEventsStatus("error");
        return { status: "error", error };
      }
    },
    [authUser?.uid]
  );

  // Ne charge que si jamais charge cette session (status "idle") : a appeler
  // a l'ouverture reelle de l'Agenda, pas au login (cf. section 21/22 du
  // contre-audit : "l'historique est charge trop tot et trop lourdement").
  // Identite STABLE (lit historyEventsStatusRef plutot que de dependre de
  // l'etat reactif) pour ne pas re-declencher les effets consommateurs a
  // chaque transition de statut (cf. note sur historyEventsStatusRef).
  const ensureHistoryEventsLoaded = useCallback(
    ({ signal } = {}) => {
      if (historyEventsStatusRef.current !== "idle") {
        return Promise.resolve({ status: "already-loaded" });
      }
      // Verrou synchrone : un prechargement idle et un clic utilisateur
      // peuvent arriver dans la meme frame avant le prochain rendu React.
      historyEventsStatusRef.current = "loading";
      return loadHistoryEventsFirstPage({ signal });
    },
    [loadHistoryEventsFirstPage]
  );

  // Force un rechargement complet de la 1ere page (pull-to-refresh, ou apres
  // une mutation join/leave dont on ne peut pas patcher finement l'etat local
  // — cf. section 21.5/syncEventFromParticipation plus bas).
  const refreshHistoryEvents = useCallback(
    ({ signal } = {}) => loadHistoryEventsFirstPage({ signal }),
    [loadHistoryEventsFirstPage]
  );

  // Page suivante de l'historique : pas de plafond silencieux a 300, le
  // frontend doit explicitement demander la suite (cf. C21/22, "Le frontend
  // s'arrete silencieusement a 300 evenements sans charger plus").
  const loadMoreHistoryEvents = useCallback(
    async ({ signal } = {}) => {
      if (!authUser?.uid || !historyEventsNextCursor || isHistoryEventsLoadingMore) {
        return { status: "skipped" };
      }

      setIsHistoryEventsLoadingMore(true);
      // ADR-005 §9 : une page suivante d'une session precedente ne touche ni
      // l'historique ni l'indicateur de chargement de la nouvelle session.
      const requestGeneration = sessionGenerationRef.current;
      const isStale = () => isStaleSessionResponse(requestGeneration, sessionGenerationRef.current);

      try {
        const page = await loadMoreMyEventsRuntimeData({
          cursor: historyEventsNextCursor,
          signal,
        });

        if (isStale()) {
          return { status: "aborted" };
        }

        if (signal?.aborted) {
          setIsHistoryEventsLoadingMore(false);
          return { status: "aborted" };
        }

        startTransition(() => {
          setHistoryEvents((prev) => mergeUniqueEventsById(prev, page.events || []));
          setHistoryEventsNextCursor(page.nextCursor || null);
          setIsHistoryEventsLoadingMore(false);
        });

        return { status: "success" };
      } catch (error) {
        if (isStale()) {
          return { status: "aborted" };
        }

        setIsHistoryEventsLoadingMore(false);

        if (isAbortError(error) || signal?.aborted) {
          return { status: "aborted" };
        }

        console.error("Erreur chargement page suivante historique:", error);
        return { status: "error", error };
      }
    },
    [authUser?.uid, historyEventsNextCursor, isHistoryEventsLoadingMore]
  );

  // Synchronisation optimiste de historyEvents apres une mutation locale
  // (creation/maj) dont on connait deja l'objet event complet — evite un
  // aller-retour serveur quand ce n'est pas necessaire (cf. section 21/22).
  const upsertHistoryEvent = useCallback((event) => {
    if (!event?.id) return;
    startTransition(() => {
      setHistoryEvents((prev) => upsertEventInList(prev, event));
    });
  }, []);

  const patchHistoryEvent = useCallback((eventId, patch) => {
    if (!eventId || !patch) return;
    startTransition(() => {
      setHistoryEvents((prev) =>
        prev.map((eventItem) =>
          eventItem.id === eventId ? mergeEventPatch(eventItem, patch) : eventItem
        )
      );
    });
  }, []);

  const removeHistoryEventById = useCallback((eventId) => {
    if (!eventId) return;
    startTransition(() => {
      setHistoryEvents((prev) => removeEventFromList(prev, eventId));
    });
  }, []);

  const refreshSecretEvents = useCallback(
    async ({ signal, silent = false } = {}) => {
      if (!canViewSecretEvents) {
        startTransition(() => {
          setSecretEvents([]);
          setHasLoadedSecretEvents(false);
          setIsSecretEventsLoading(false);
        });
        return { status: "forbidden" };
      }

      if (!silent) {
        setIsSecretEventsLoading(true);
      }

      try {
        const payload = await loadSecretEventsRuntimeData({ signal });

        if (signal?.aborted) {
          return { status: "aborted" };
        }

        startTransition(() => {
          setSecretEvents(payload.events || []);
          setHasLoadedSecretEvents(true);
          if (!silent) {
            setIsSecretEventsLoading(false);
          }
        });

        return { status: "success" };
      } catch (error) {
        if (isAbortError(error) || signal?.aborted) {
          return { status: "aborted" };
        }

        console.error("Erreur chargement events secrets:", error);
        if (!silent) {
          setSecretEvents([]);
          setHasLoadedSecretEvents(true);
          setIsSecretEventsLoading(false);
        }

        return { status: "error", error };
      }
    },
    [canViewSecretEvents]
  );

  const refreshEventById = useCallback(
    async (eventId, { signal, applyAfter } = {}) => {
      if (!eventId) {
        return { status: "invalid-event-id" };
      }

      const requestGeneration = sessionGenerationRef.current;
      const requestSequence = ++eventDetailRequestSequenceRef.current;
      setEventDetailRequests((current) => ({ ...current, [eventId]: { requestSequence, requestGeneration } }));
      try {
        // ADR-005 §9 : un refus (403) ou une absence (404) de la fiche
        // publique ne court-circuite ni le repli /v1/events/mine ni le
        // nettoyage de la fiche ouverte en cas de perte d'acces.
        let event = null;
        try {
          event = await getRemoteEvent(eventId, { signal });
        } catch (error) {
          if (!isAccessLossError(error)) {
            throw error;
          }
        }

        if (signal?.aborted || isStaleSessionResponse(requestGeneration, sessionGenerationRef.current)) {
          return { status: "aborted" };
        }

        // Fallback pour les événements passés/annulés/archivés : le endpoint public
        // exige status=PUBLISHED — pour les organisateurs et participants, on retombe
        // sur le endpoint privé qui accepte tous les statuts (C31).
        if (!event?.id) {
          try {
            event = await getMyRemoteEventById(eventId, { signal });
          } catch (error) {
            // Utilisateur non connecté (401) ou sans accès (403/404) : la
            // sortie est introuvable pour lui. Une erreur reseau ou serveur
            // remonte au catch general et ne purge rien.
            if (!isAccessLossError(error) && error?.status !== 401) {
              throw error;
            }
          }
        }

        if (signal?.aborted || isStaleSessionResponse(requestGeneration, sessionGenerationRef.current)) {
          return { status: "aborted" };
        }

        if (!event?.id) {
          // ADR-005 §9 : perte d'acces (retrait, visibilite changee) — la fiche
          // ouverte et les listes en memoire ne gardent pas l'ancienne copie.
          startTransition(() => {
            // Purge quelle que soit l'ancienne visibilite : un 404 ne distingue
            // pas une sortie publique annulee d'une sortie devenue privee.
            setEvents((previousEvents) => removeEventFromList(previousEvents, eventId));
            setSecretEvents((previousEvents) => removeEventFromList(previousEvents, eventId));
            setSelectedEvent((previousSelectedEvent) =>
              previousSelectedEvent?.id === eventId ? null : previousSelectedEvent
            );
          });
          return { status: "not-found" };
        }

        // The request starts at opening; only its successful UI update waits.
        // Access loss is handled above, without retaining stale private data.
        if (applyAfter && !isEventAccessLocked(event)) {
          await applyAfter;
          if (signal?.aborted || isStaleSessionResponse(requestGeneration, sessionGenerationRef.current)) {
            return { status: "aborted" };
          }
        }
        const preservePublicRanking =
          eventDiscoveryAreaRef.current?.type === "popular" ||
          eventDiscoveryAreaRef.current?.type === "nearest" ||
          eventDiscoveryAreaRef.current?.type === "national";
        startTransition(() => {
          if (isSecretEvent(event)) {
            setEvents((previousEvents) => removeEventFromList(previousEvents, event.id));
            setSecretEvents((previousEvents) => upsertEventInList(previousEvents, event));
          } else {
            setEvents((previousEvents) =>
              upsertEventInList(previousEvents, event, {
                preservePosition: preservePublicRanking,
              })
            );
            setSecretEvents((previousEvents) => removeEventFromList(previousEvents, event.id));
          }

          setSelectedEvent((previousSelectedEvent) =>
            applyRefreshedEventToSelection(previousSelectedEvent, event)
          );
        });

        return { status: "success", event };
      } catch (error) {
        if (isAbortError(error) || signal?.aborted) {
          return { status: "aborted" };
        }

        console.error("Erreur rafraichissement event:", error);
        return { status: "error", error };
      } finally {
        setEventDetailRequests((current) => {
          if (current[eventId]?.requestSequence !== requestSequence) return current;
          const next = { ...current };
          delete next[eventId];
          return next;
        });
      }
    },
    []
  );

  const unlockEventAccess = useCallback(
    async (eventId, accessCode, { signal } = {}) => {
      if (!eventId) {
        return { status: "invalid-event-id" };
      }

      const requestGeneration = sessionGenerationRef.current;
      try {
        const event = await getRemoteEvent(eventId, { signal, accessCode });

        // ADR-005 §9 : ni code memorise ni fiche appliquee pour un autre compte.
        if (signal?.aborted || isStaleSessionResponse(requestGeneration, sessionGenerationRef.current)) {
          return { status: "aborted" };
        }

        if (!event?.id) {
          return { status: "not-found" };
        }

        if (isEventAccessLocked(event)) {
          clearStoredEventAccessCode(eventId);
          return { status: "invalid-code", event };
        }

        storeEventAccessCode(eventId, accessCode);

        const preservePublicRanking =
          eventDiscoveryAreaRef.current?.type === "popular" ||
          eventDiscoveryAreaRef.current?.type === "nearest" ||
          eventDiscoveryAreaRef.current?.type === "national";
        startTransition(() => {
          if (isSecretEvent(event)) {
            setEvents((previousEvents) => removeEventFromList(previousEvents, event.id));
            setSecretEvents((previousEvents) => upsertEventInList(previousEvents, event));
          } else {
            setEvents((previousEvents) =>
              upsertEventInList(previousEvents, event, {
                preservePosition: preservePublicRanking,
              })
            );
            setSecretEvents((previousEvents) => removeEventFromList(previousEvents, event.id));
          }
          setSelectedEvent(event);
        });

        return { status: "success", event };
      } catch (error) {
        if (isAbortError(error) || signal?.aborted) {
          return { status: "aborted" };
        }

        return { status: "error", error };
      }
    },
    []
  );

  /* ====================================================
     INITIALISATION
  ==================================================== */

  useEffect(() => {
    if (canViewSecretEvents) {
      return;
    }

    startTransition(() => {
      setSecretEvents([]);
      setHasLoadedSecretEvents(false);
      setIsSecretEventsLoading(false);
    });
  }, [canViewSecretEvents]);

  useEffect(() => {
    const abortController =
      typeof AbortController === "function" ? new AbortController() : null;

    mark("events_request_start");
    refreshEvents({ signal: abortController?.signal });

    return () => {
      abortController?.abort();
    };
  }, [refreshEvents]);

  // Reinitialise (sans fetch immediat) l'historique personnel a chaque
  // connexion/deconnexion/changement de compte. Le chargement reel n'a lieu
  // qu'a la premiere ouverture de l'Agenda, via `ensureHistoryEventsLoaded`
  // (cf. section 21/22 du contre-audit : ne pas charger 300 events lourds au
  // login, en concurrence avec le chargement de la carte).
  useEffect(() => {
    // Nouvelle session : une requete d'historique de la session precedente
    // ne peut plus laisser son statut ni son indicateur de chargement.
    historyEventsStatusRef.current = "idle";
    startTransition(() => {
      setHistoryEvents([]);
      setHistoryEventsNextCursor(null);
      setHistoryEventsStatus("idle");
      setIsHistoryEventsLoadingMore(false);
    });
  }, [authUser?.uid]);

  // ADR-005 §9 : chaque changement de compte ouvre une nouvelle session
  // (requetes en cours ignorees) ; changement de compte ou deconnexion ->
  // codes d'acces memorises, fiche ouverte et sorties a lien purges. La liste
  // publique est rechargee par l'effet de re-fetch ci-dessous.
  useLayoutEffect(() => {
    const previousUid = authUidRef.current;
    const nextUid = authUser?.uid || null;
    authUidRef.current = nextUid;
    if (previousUid !== nextUid) {
      setEventDetailRequests({});
      sessionGenerationRef.current += 1;
      eventsRequestGenerationRef.current += 1;
      setEvents([]);
      // La liste est rechargee juste apres (effet de re-fetch, silencieux) :
      // sans ce drapeau, la liste vide affichait « Aucune sortie » pendant
      // ~1 s a l'ouverture de l'appli, le temps que le compte se charge.
      setIsEventsLoading(true);
      setSelectedEvent(null);
      setSecretEvents([]);
    }
    if (!shouldPurgeOnAccountTransition(previousUid, nextUid)) {
      return;
    }
    clearAllStoredEventAccessCodes();
    setSelectedEvent(null);
    setSecretEvents([]);
  }, [authUser?.uid]);

  // Re-fetch silencieux quand le viewer ou son profil change, sans transformer
  // automatiquement le classement populaire en recherche locale.
  const isFirstLocationEffectRef = useRef(true);
  useEffect(() => {
    if (isFirstLocationEffectRef.current) {
      isFirstLocationEffectRef.current = false;
      return undefined;
    }

    const abortController =
      typeof AbortController === "function" ? new AbortController() : null;

    refreshEvents({ signal: abortController?.signal, silent: true });

    return () => {
      abortController?.abort();
    };
  }, [authUser?.uid, userGeoLat, userGeoLng, userMaxDist, refreshEvents]);

  useEffect(() => {
    const pendingSharedEventId = pendingSharedEventIdRef.current;

    // La restauration Firebase peut changer le viewer apres le premier rendu.
    // Attendre sa resolution evite que la purge de session efface la fiche
    // partagee juste apres son ouverture ; les droits sont lus pour ce viewer.
    if (authLoading || !pendingSharedEventId) {
      return undefined;
    }

    if (!sharedLandingTrackedRef.current) {
      sharedLandingTrackedRef.current = true;
      const attribution = readShareAttribution();
      trackConversionEvent("shared_event_landing_viewed", {
        event_id: pendingSharedEventId,
        share_source: attribution.shareSource || "direct",
      });
    }

    const abortController =
      typeof AbortController === "function" ? new AbortController() : null;
    let isCancelled = false;

    refreshEventById(pendingSharedEventId, { signal: abortController?.signal })
      .then((result) => {
        if (!isCancelled && result?.status === "success" && result.event?.id) {
          pendingSharedEventIdRef.current = null;
          sharedLoadedSelectionRef.current = result.event;
          trackConversionEvent("shared_event_detail_loaded", {
            event_id: result.event.id,
            share_source: readShareAttribution().shareSource || "direct",
          });
          setSelectedEvent(result.event);
        }
      })
      .catch(() => null);

    return () => {
      isCancelled = true;
      abortController?.abort();
    };
  }, [authLoading, authUser?.uid, refreshEventById]);

  useEffect(() => {
    if (!selectedEvent?.id) {
      sharedLoadedSelectionRef.current = null;
      return undefined;
    }

    // A direct link has just read the detail for this exact selection.
    if (sharedLoadedSelectionRef.current === selectedEvent) {
      sharedLoadedSelectionRef.current = null;
      return undefined;
    }

    const abortController =
      typeof AbortController === "function" ? new AbortController() : null;

    let release;
    const applyAfter = new Promise((resolve) => { release = resolve; });
    const timer = setTimeout(release, SELECTED_EVENT_REFRESH_DELAY_MS);
    refreshEventById(selectedEvent.id, { signal: abortController?.signal, applyAfter });

    return () => {
      clearTimeout(timer);
      abortController?.abort();
      release();
    };
  }, [refreshEventById, selectedEvent?.id]);

  useEffect(() => {
    if (!selectedEvent?.id || typeof window === "undefined") {
      return undefined;
    }

    let abortController = null;
    const refreshSelectedEvent = () => {
      abortController?.abort?.();
      abortController =
        typeof AbortController === "function" ? new AbortController() : null;
      refreshEventById(selectedEvent.id, { signal: abortController?.signal });
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshSelectedEvent();
      }
    };
    const handleTransportChange = (e) => {
      if (e.detail?.eventId === selectedEvent.id) refreshSelectedEvent();
    };

    window.addEventListener("focus", refreshSelectedEvent);
    window.addEventListener("transport-needs-changed", handleTransportChange);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    const intervalId = window.setInterval(refreshSelectedEvent, 45000);

    return () => {
      abortController?.abort?.();
      window.removeEventListener("focus", refreshSelectedEvent);
      window.removeEventListener("transport-needs-changed", handleTransportChange);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.clearInterval(intervalId);
    };
  }, [refreshEventById, selectedEvent?.id]);

  /* ====================================================
     EVENTS VISIBLES (MAP ↔ LIST)
  ==================================================== */

  const rawVisibleEvents = useMemo(() => {
    return filterVisibleUpcomingEvents(events, mapBounds, { secret: false, eventDiscoveryArea });
  }, [events, mapBounds, eventDiscoveryArea]);

  const visibleEventsRef = useRef(rawVisibleEvents);
  if (!sameEventIds(rawVisibleEvents, visibleEventsRef.current)) {
    visibleEventsRef.current = rawVisibleEvents;
  }
  const visibleEvents = visibleEventsRef.current;

  const rawVisibleSecretEvents = useMemo(() => {
    return filterVisibleUpcomingEvents(secretEvents, mapBounds, { secret: true });
  }, [mapBounds, secretEvents]);

  const visibleSecretEventsRef = useRef(rawVisibleSecretEvents);
  if (!sameEventIds(rawVisibleSecretEvents, visibleSecretEventsRef.current)) {
    visibleSecretEventsRef.current = rawVisibleSecretEvents;
  }
  const visibleSecretEvents = visibleSecretEventsRef.current;

  /* ====================================================
     INTENTIONS — INSCRIPTION
  ==================================================== */

  /* ====================================================
     INTENTIONS - MISE A JOUR EVENEMENT
  ==================================================== */

  const updateEvent = useCallback(async (eventId, patch) => {
    if (!eventId || !patch) {
      return false;
    }

    const sanitizedPatch = sanitizeDeepStringValues(patch);

    const updatedEvent = await updateRemoteEvent(eventId, sanitizedPatch);
    const resolvedPatch = updatedEvent?.id ? updatedEvent : sanitizedPatch;
    const preservePublicRanking =
      eventDiscoveryAreaRef.current?.type === "popular" ||
      eventDiscoveryAreaRef.current?.type === "nearest" ||
      eventDiscoveryAreaRef.current?.type === "national";

    startTransition(() => {
      if (updatedEvent?.id) {
        if (isSecretEvent(updatedEvent)) {
          setEvents((prevEvents) => removeEventFromList(prevEvents, eventId));
          setSecretEvents((prevEvents) => upsertEventInList(prevEvents, updatedEvent));
        } else {
          setEvents((prevEvents) =>
            upsertEventInList(prevEvents, updatedEvent, {
              preservePosition: preservePublicRanking,
            })
          );
          setSecretEvents((prevEvents) => removeEventFromList(prevEvents, eventId));
        }
      } else {
        setEvents((prevEvents) =>
          prevEvents.map((eventItem) =>
            eventItem.id === eventId ? mergeEventPatch(eventItem, resolvedPatch) : eventItem
          )
        );
        setSecretEvents((prevEvents) =>
          prevEvents.map((eventItem) =>
            eventItem.id === eventId ? mergeEventPatch(eventItem, resolvedPatch) : eventItem
          )
        );
      }

      setSelectedEvent((prevSelectedEvent) => {
        if (!prevSelectedEvent || prevSelectedEvent.id !== eventId) {
          return prevSelectedEvent;
        }

        return mergeEventPatch(prevSelectedEvent, resolvedPatch);
      });
    });

    // Si l'Agenda a deja ete ouvert cette session, repercute la modification
    // (annulation, changement de date...) sans attendre un rechargement
    // complet — cf. synchronisation historyEvents, section 21/22 du contre-audit.
    if (historyEventsStatusRef.current !== "idle") {
      patchHistoryEvent(eventId, resolvedPatch);
    }

    if (preservePublicRanking) {
      refreshEvents({ silent: true });
    }

    // La sortie sauvegardee par le serveur (ex. pour la fenetre de diffusion
    // apres publication) ; sinon true. Les appelants testent `=== false`.
    return updatedEvent?.id ? updatedEvent : true;
  }, [patchHistoryEvent, refreshEvents]);

  const createEvent = useCallback(async (payload = {}) => {
    const sanitizedPayload = sanitizeDeepStringValues(payload);
    const createdEvent = await createRemoteEvent(sanitizedPayload);

    if (!createdEvent?.id) {
      return null;
    }

    const preservePublicRanking =
      eventDiscoveryAreaRef.current?.type === "popular" ||
      eventDiscoveryAreaRef.current?.type === "nearest" ||
      eventDiscoveryAreaRef.current?.type === "national";
    startTransition(() => {
      if (isSecretEvent(createdEvent)) {
        setEvents((previousEvents) => removeEventFromList(previousEvents, createdEvent.id));
        setSecretEvents((previousEvents) => upsertEventInList(previousEvents, createdEvent));
      } else {
        setEvents((previousEvents) =>
          upsertEventInList(previousEvents, createdEvent, {
            preservePosition: preservePublicRanking,
          })
        );
        setSecretEvents((previousEvents) => removeEventFromList(previousEvents, createdEvent.id));
      }
      setSelectedEvent(createdEvent);
    });

    // Le viewer est forcement l'organisateur de l'event qu'il vient de creer :
    // upsert direct sans aller-retour serveur, si l'Agenda est deja charge.
    if (historyEventsStatusRef.current !== "idle") {
      upsertHistoryEvent({ ...createdEvent, viewerRole: "organizer", viewerParticipationStatus: null });
    }

    if (preservePublicRanking) {
      refreshEvents({ silent: true });
    }

    return createdEvent;
  }, [refreshEvents, upsertHistoryEvent]);

  const incrementEventAttendees = useCallback((eventId) => {
    startTransition(() => {
      setEvents((prev) =>
        prev.map((evt) =>
          evt.id === eventId
            ? { ...evt, attendeesCount: (evt.attendeesCount || 0) + 1 }
            : evt
        )
      );
      setSecretEvents((prev) =>
        prev.map((evt) =>
          evt.id === eventId
            ? { ...evt, attendeesCount: (evt.attendeesCount || 0) + 1 }
            : evt
        )
      );
      setSelectedEvent((prev) =>
        prev?.id === eventId
          ? { ...prev, attendeesCount: (prev.attendeesCount || 0) + 1 }
          : prev
      );
    });
  }, []);

  const decrementEventAttendees = useCallback((eventId) => {
    startTransition(() => {
      setEvents((prev) =>
        prev.map((evt) =>
          evt.id === eventId
            ? { ...evt, attendeesCount: Math.max(0, (evt.attendeesCount || 0) - 1) }
            : evt
        )
      );
      setSecretEvents((prev) =>
        prev.map((evt) =>
          evt.id === eventId
            ? { ...evt, attendeesCount: Math.max(0, (evt.attendeesCount || 0) - 1) }
            : evt
        )
      );
      setSelectedEvent((prev) =>
        prev?.id === eventId
          ? { ...prev, attendeesCount: Math.max(0, (prev.attendeesCount || 0) - 1) }
          : prev
      );
    });
  }, []);

  const syncEventFromParticipation = useCallback((eventId, patch = {}, { skipSelectedEvent = false } = {}) => {
    if (!eventId || !patch || Object.keys(patch).length === 0) {
      return;
    }

    startTransition(() => {
      setEvents((prevEvents) =>
        prevEvents.map((eventItem) =>
          eventItem.id === eventId ? mergeEventPatch(eventItem, patch) : eventItem
        )
      );
      setSecretEvents((prevEvents) =>
        prevEvents.map((eventItem) =>
          eventItem.id === eventId ? mergeEventPatch(eventItem, patch) : eventItem
        )
      );

      if (!skipSelectedEvent) {
        setSelectedEvent((prevSelectedEvent) => {
          if (!prevSelectedEvent || prevSelectedEvent.id !== eventId) {
            return prevSelectedEvent;
          }

          return mergeEventPatch(prevSelectedEvent, patch);
        });
      }
    });

    // syncEventFromParticipation est appelee aussi bien apres un join qu'une
    // desinscription/annulation de participation : on ne sait pas ici lequel
    // sans dupliquer la logique d'appel. Plutot que de patcher finement (et
    // risquer un etat incoherent), on refait un fetch serveur de la 1ere page
    // de l'Agenda — strategie "refresh serveur" documentee en section 21/22
    // du contre-audit — mais seulement si l'Agenda a deja ete ouvert cette
    // session (sinon ensureHistoryEventsLoaded() la chargera a la demande,
    // deja a jour).
    if (historyEventsStatusRef.current !== "idle") {
      refreshHistoryEvents().catch(() => {});
    }
  }, [refreshHistoryEvents]);

  /* ====================================================
     VALEUR CONTEXTE
  ==================================================== */

  const value = useMemo(
    () => ({
      /* ---------- données ---------- */
      events,
      isEventsLoading,
      historyEvents,
      historyEventsStatus,
      isHistoryEventsLoading,
      hasLoadedHistoryEvents,
      isHistoryEventsLoadingMore,
      hasMoreHistoryEvents: Boolean(historyEventsNextCursor),
      secretEvents,
      isSecretEventsLoading,
      hasLoadedSecretEvents,
      canViewSecretEvents,
      refreshEvents,
      ensureHistoryEventsLoaded,
      refreshHistoryEvents,
      loadMoreHistoryEvents,
      refreshSecretEvents,
      refreshEventById,
      unlockEventAccess,
      pendingDeepLink,
      consumePendingDeepLink,

      /* ---------- sélections ---------- */
      selectedEvent,
      isSelectedEventDetailLoading: Boolean(eventDetailRequests[selectedEvent?.id]?.requestGeneration === sessionGenerationRef.current),
      setSelectedEvent,

      /* ---------- carte ---------- */
      setMapBounds,
      setMapZoom,
      visibleEvents,
      visibleSecretEvents,
      eventDiscoveryArea,
      applyMapCircleSearchArea,
      applyNearestEventsDiscovery,
      applyNationalEventsDiscovery,
      applyPopularEventsDiscovery,
      clearMapCircleSearchArea,

      /* ---------- mutations event ---------- */
      createEvent,
      updateEvent,
      incrementEventAttendees,
      decrementEventAttendees,
      syncEventFromParticipation,
    }),
    [
      events,
      isEventsLoading,
      historyEvents,
      historyEventsStatus,
      isHistoryEventsLoading,
      hasLoadedHistoryEvents,
      isHistoryEventsLoadingMore,
      historyEventsNextCursor,
      secretEvents,
      isSecretEventsLoading,
      hasLoadedSecretEvents,
      canViewSecretEvents,
      refreshEvents,
      ensureHistoryEventsLoaded,
      refreshHistoryEvents,
      loadMoreHistoryEvents,
      refreshSecretEvents,
      refreshEventById,
      unlockEventAccess,
      pendingDeepLink,
      consumePendingDeepLink,
      selectedEvent,
      eventDetailRequests,
      visibleEvents,
      visibleSecretEvents,
      eventDiscoveryArea,
      applyMapCircleSearchArea,
      applyNearestEventsDiscovery,
      applyNationalEventsDiscovery,
      applyPopularEventsDiscovery,
      clearMapCircleSearchArea,
      incrementEventAttendees,
      decrementEventAttendees,
      syncEventFromParticipation,
      createEvent,
      updateEvent,
    ]
  );

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
}

/* ======================================================
   HOOK D’ACCÈS SÉCURISÉ
====================================================== */

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) {
    throw new Error("useApp must be used inside AppProvider");
  }
  return ctx;
}
